import { setAiAgentSuperAdminDomainAllowed } from '../../pure-js/libs/isSuperUser'
import { getFeatureFlagsFromUrl } from './shared/libs/featureFlags'

export type ConfigType = typeof defaultConfig

type PartialConfig = Partial<typeof defaultConfig>

type ViteEnv = {
  MODE?: string
  VITE_ENVIRONMENT?: string
  VITE_GAME_BASE_URL?: string
}

const importMetaEnv = (import.meta as unknown as { env?: ViteEnv }).env

// Use Vite's built-in MODE: 'development' | 'production' | 'test'
const viteMode = importMetaEnv?.MODE || 'production'

// Map Vite's MODE to our config environments
let env: string
if (process.env.NODE_ENV === 'test' || viteMode === 'test') {
  env = 'test'
} else if (viteMode === 'development') {
  env = 'local'
} else {
  // In production builds, check VITE_ENVIRONMENT first
  // This allows deployed dev environment to use development config
  const envOverride = importMetaEnv?.VITE_ENVIRONMENT
  if (envOverride === 'development' || envOverride === 'dev') {
    env = 'development'
  } else {
    env = envOverride || 'prod'
  }
}

export const defaultConfig = {
  // System Configuration
  name: 'default',
  appUrl: 'http://localhost:5173',
  sentryDsn: 'https://ef4ccf693dc594c40afb04bfa911d1f3@o4506297061015552.ingest.us.sentry.io/4507678285103104',
  enableLogFirestoreQueryResult: false,
  enableErrorMessagesInNotifications: false,
  enableServiceUnavilablePage: false,
  enableLogFirebaseDBError: false,
  enableSentry: false,
  enableGoogleLogin: true,
  enableOverrideTeamId: true,
  enableOnboarding: true,
  enableShorterAssessment: false,
  enableUidOnProfilePage: true,
  enableSendTriggerReflectionEmailButton: false,
  enableInactiveTimeExperimental: false,
  enableAnalytics: false,
  enableDevelop: true, // Enabled by default for new project
  gameBaseUrl: '', // Absolute prefix for game iframe; empty = same-origin
  enableCall: true, // Enabled by default for new project
  enableLoggingOfConfig: false,
  // Report client errors to Slack via the reportClientError callable. On by default in every
  // environment — dev errors land in the `-dev` channels (the backend maps them by project).
  enableSlackErrorReporting: true,

  // UI Feature Flags (used as defaults for Redux store)
  enableGamer: true,
  enableFacilitator: true,
  enableLanguageSwitcher: false,
  enableLatestSessionFeedback: true,
  enableGenerateReports: true,
  enableExperimentalTeamFeedback: false,
  enableAchievements: true
}

export const local: PartialConfig = {
  ...defaultConfig,
  name: 'local',
  enableLogFirestoreQueryResult: true,
  enableErrorMessagesInNotifications: true,
  enableLogFirebaseDBError: true,
  enableOnboarding: true,
  enableShorterAssessment: true,
  enableSendTriggerReflectionEmailButton: false,
  enableCall: true,
  enableDevelop: true,
  // Local default should not depend on external DNS; use same-origin game path unless explicitly overridden.
  gameBaseUrl: importMetaEnv?.VITE_GAME_BASE_URL ?? '',
  // UI Feature Flags for local development
  enableGamer: true,
  enableFacilitator: true,
  enableLanguageSwitcher: false,
  enableLatestSessionFeedback: true,
  enableGenerateReports: true,
  enableExperimentalTeamFeedback: true,
  enableAchievements: true
}

const test: PartialConfig = {
  ...defaultConfig,
  name: 'test',
  enableSlackErrorReporting: false, // Unit tests must not call out to the backend
  enableAnalytics: false,
  enableDevelop: false,
  enableCall: true,
  // UI Feature Flags for testing
  enableGamer: true,
  enableFacilitator: true,
  enableLanguageSwitcher: false,
  enableLatestSessionFeedback: false,
  enableGenerateReports: false
}

const beta: PartialConfig = {
  ...defaultConfig,
  name: 'beta',
  enableCall: true,
  enableDevelop: false,
  // UI Feature Flags for beta
  enableGamer: true,
  enableFacilitator: true,
  enableLanguageSwitcher: false,
  enableLatestSessionFeedback: false,
  enableGenerateReports: false
}

const development: PartialConfig = {
  ...defaultConfig,
  name: 'development',
  // Development-specific settings (deployed to intraction-dev.web.app)
  enableLogFirestoreQueryResult: true, // More logging in dev
  enableErrorMessagesInNotifications: true, // Show errors in dev
  enableLogFirebaseDBError: true, // Log DB errors in dev
  enableSentry: false, // Disable Sentry in dev
  enableSlackErrorReporting: true, // Dev errors go to the -dev ops channels
  enableDevelop: true, // Enable dev features
  gameBaseUrl: '',
  enableCall: true,
  enableOnboarding: true,
  enableShorterAssessment: true, // Enable experimental features
  enableSendTriggerReflectionEmailButton: true, // Dev tools
  enableExperimentalTeamFeedback: true, // Experimental features
  // UI Feature Flags for development
  enableGamer: true,
  enableFacilitator: true,
  enableLanguageSwitcher: true, // Enable in dev for testing
  enableLatestSessionFeedback: true,
  enableGenerateReports: true,
  enableAchievements: true
}

export const prod: PartialConfig = {
  ...defaultConfig,
  name: 'prod',
  enableSentry: true,
  enableSlackErrorReporting: true,
  enableDevelop: false,
  gameBaseUrl: '',
  enableCall: true,
  // UI Feature Flags for production
  enableGamer: true,
  enableFacilitator: true,
  enableLanguageSwitcher: true,
  enableLatestSessionFeedback: true,
  enableGenerateReports: true
}

const configs: { [key: string]: PartialConfig } = { local, prod, test, beta, development }

const envConfig = configs[env] || prod

// Read before the URL flags are merged: `?enableDevelop=true` must not be able to turn this on.
setAiAgentSuperAdminDomainAllowed(envConfig.enableDevelop === true)

const urlFlags = getFeatureFlagsFromUrl()

const config = { ...envConfig, ...urlFlags }

export default config as typeof defaultConfig
